import React, { useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import UploadGrid from "../../RequestForApproval/components/UploadGrid";
import { isoDay } from "./helpers";

const UPLOAD_SECTIONS = [
  {
    key: "shopDrawings",
    title: "Shop Drawings",
    icon: (
      <ArchitectureOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
    ),
  },
  {
    key: "boqs",
    title: "BOQs",
    icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />,
  },
  {
    key: "materialSpecs",
    title: "Material Specifications",
    icon: <CategoryOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />,
  },
];

export default function TimelineTab({ deliverables, setDeliverables }) {
  const [uploadState, setUploadState] = useState(() => {
    const init = {};
    UPLOAD_SECTIONS.forEach((s) => {
      init[s.key] = { pdfs: [], images: [] };
    });
    return init;
  });

  const update = (idx, patch) => {
    setDeliverables((prev) =>
      prev.map((d, i) => {
        if (i !== idx) return d;
        const next = { ...d, ...patch };
        // Editing start/end dates counts as a modification — only
        // bump to MODIFIED if it's not already ADDED/DELETED.
        if (next.status !== "ADDED" && next.status !== "DELETED") {
          next.status = "MODIFIED";
        }
        return next;
      }),
    );
  };

  const handlePdfsChange = (key) => (next) =>
    setUploadState((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), pdfs: next },
    }));

  const handleImagesChange = (key) => (next) =>
    setUploadState((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), images: next },
    }));

  // The timeline section is an editor for live deliverables only —
  // soft-deleted entries (status DELETED) are hidden here. We still
  // track their original index in the full array so date edits patch
  // the correct slot.
  const visibleDels = (
    Array.isArray(deliverables) ? deliverables : []
  )
    .map((d, originalIdx) => ({ d, originalIdx }))
    .filter(({ d }) => d.status !== "DELETED");
  const hasDels = visibleDels.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Timeline section */}
      <Box
        sx={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          p: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: "#4D6BF6" }} />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
            }}
          >
            Timeline
          </Typography>
        </Box>

        {!hasDels ? (
          <Typography
            sx={{
              fontSize: 13,
              color: "#9CA3AF",
              fontFamily: "Inter Tight",
              py: 1,
            }}
          >
            No deliverables to schedule.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {visibleDels.map(({ d, originalIdx }, idx) => (
              <Box
                key={d.id || originalIdx}
                sx={{
                  display: "grid",
                  // Mobile: 2-col grid so the two date pickers sit
                  // side-by-side under the deliverable name.
                  gridTemplateColumns: { xs: "1fr 1fr", md: "1.5fr 1fr 1fr" },
                  gap: 2,
                  alignItems: "center",
                  p: 1.5,
                  borderRadius: "10px",
                  border: "1px solid #F1F2F4",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#0D0D0E",
                    fontWeight: 500,
                    fontFamily: "Inter Tight",
                    gridColumn: { xs: "1 / -1", md: "auto" },
                  }}
                >
                  {d.deliverable_description ||
                    d.name ||
                    `Deliverable ${idx + 1}`}
                </Typography>
                <TextField
                  type="date"
                  label="Start Date"
                  value={isoDay(d.start_date)}
                  onChange={(e) =>
                    update(originalIdx, { start_date: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontFamily: "Inter Tight",
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={isoDay(d.end_date)}
                  onChange={(e) =>
                    update(originalIdx, { end_date: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontFamily: "Inter Tight",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Upload sections — Shop Drawings, BOQs, Material Specs.
          Each section takes its own full-width row (vertical stack).
          On mobile, the thumbnails inside each section don't wrap;
          they live inside the card as a horizontal-scroll strip
          (handled by UploadGrid via the `mobileScroll` flag). */}
      {UPLOAD_SECTIONS.map(({ key, title, icon }) => (
        <Box
          key={key}
          sx={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            p: 2.5,
          }}
        >
          <UploadGrid
            title={title}
            subtitle="Upload images & PDFs"
            icon={icon}
            pdfFiles={uploadState[key]?.pdfs || []}
            onPdfFilesChange={handlePdfsChange(key)}
            images={uploadState[key]?.images || []}
            onImagesChange={handleImagesChange(key)}
            mobileScroll
          />
        </Box>
      ))}
    </Box>
  );
}
